export const validateCPF = (value: string, min = 0, max = 11): boolean => {
  value = value.toString().replace(/[^0-9]+/g, '');
  if (typeof value === 'undefined' || (typeof value === 'string' && (value.length <= min || value.length > max))) return true;

  const calcValidatorDigit = (digits: string[]): number => {
    const calc =
      (digits.reduce((ac, value, index) => {
        return ac + parseInt(value) * (digits.length + 1 - index);
      }, 0) *
        10) %
      11;

    return calc === 10 ? 0 : calc;
  };

  const cpf = Array.from(value.padStart(max, '0').substr(0, max));

  const digit1 = parseInt(cpf[9]);
  const digit2 = parseInt(cpf[10]);

  const digit1calc = calcValidatorDigit(cpf.slice(0, 9));
  const digit2calc = calcValidatorDigit(cpf.slice(0, 10));
  const isFalseValid = cpf.every(v => v === cpf[0]);

  return !isFalseValid && digit1 === digit1calc && digit2 === digit2calc;
};

export const validateCNPJ = (value: string, min = 11, max = 14): boolean => {
  value = value.toString().replace(/[^0-9]+/g, '');
  if (typeof value === 'undefined' || (typeof value === 'string' && (value.length <= min || value.length > max))) return true;

  const calcValidatorDigit = (digits: string[]): number => {
    const multipliers = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const calc =
      digits.reduce((ac, value, index) => {
        return ac + parseInt(value) * multipliers[index + (multipliers.length - digits.length)];
      }, 0) % 11;

    return calc < 2 ? 0 : 11 - calc;
  };

  const cnpj = Array.from(value.padStart(max, '0').substr(0, max));

  const digit1 = parseInt(cnpj[12]);
  const digit2 = parseInt(cnpj[13]);

  const digit1calc = calcValidatorDigit(cnpj.slice(0, 12));
  const digit2calc = calcValidatorDigit(cnpj.slice(0, 13));
  const isFalseValid = cnpj.every(v => v === cnpj[0]);

  return !isFalseValid && digit1 === digit1calc && digit2 === digit2calc;
};

export type ValidatePasswordResult = {
  minimumCharacters?: boolean;
  lowerCaseCharacters?: boolean;
  upperCaseCharacters?: boolean;
  specialCharacters?: boolean;
  correctConfirmation?: boolean;
};

const DDD_CODES = [
  11, 12, 13, 14, 15, 16, 17, 18, 19, 21, 22, 24, 27, 28, 31, 32, 33, 34, 35, 37, 38, 41, 42, 43, 44, 45, 46, 47, 48, 49, 51, 53,
  54, 55, 61, 62, 64, 63, 65, 66, 67, 68, 69, 71, 73, 74, 75, 77, 79, 81, 82, 83, 84, 85, 86, 87, 88, 89, 91, 92, 93, 94, 95, 96,
  97, 98, 99,
];
export const validatePassword = (password: string): ValidatePasswordResult => ({
  /* eslint-disable no-useless-escape */
  specialCharacters: /[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/.test(password),
  upperCaseCharacters: /[A-Z]/.test(password),
  lowerCaseCharacters: /[a-z]/.test(password),
  minimumCharacters: password.length >= 8,
});

export const validateBrazilianPhone = (phone: string): boolean => {
  const cleanedPhone = phone.replace('+55', '').replace(/\D/g, '');
  const phoneDDD = cleanedPhone.substring(0, 2);
  const firstDigitAfterDDD = cleanedPhone.at(2) || '0';

  if (!(cleanedPhone.length >= 10 && cleanedPhone.length <= 11)) {
    return false;
  }

  if (cleanedPhone.length === 11 && parseInt(firstDigitAfterDDD) !== 9) {
    return false;
  }

  for (let n = 0; n < 10; n++) {
    const repeated11Number = new Array(11).join(String(n));
    const repeated12Number = new Array(12).join(String(n));

    if (cleanedPhone === repeated11Number || cleanedPhone === repeated12Number) {
      return false;
    }
  }

  if (!DDD_CODES.includes(parseInt(phoneDDD))) {
    return false;
  }

  if (cleanedPhone.length === 10 && ![2, 3, 4, 5, 7].includes(parseInt(firstDigitAfterDDD))) {
    return false;
  }

  return true;
};

export const isValidHttpUrl = (urlString: string): boolean => {
  let url: URL;

  try {
    url = new URL(urlString);
  } catch (_) {
    return false;
  }

  return url.protocol === 'http:' || url.protocol === 'https:';
};

export function isFile(input: unknown): boolean {
  if ('File' in window && input instanceof File) return true;
  else return false;
}

export function isHexColor(color: string): boolean {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
}
